import { useState, useRef, useEffect } from 'react';
import { useReceivedNotifications } from '../hooks/use-received-notifications';
import { useSentNotifications } from '../hooks/use-sent-notifications';
import SendNotificationPage from './SendNotificationPage';
import { useAuthProviderV2, useRoutesProvider } from '../../../providers/provider-hooks';
import MarkdownPreview from '../../../components/forms/raw-inputs/preview-markdown';
import PreviewTextBox from '../../../shared/components/text/PreviewTextBox';
import { SpringPagination } from '../../../shared/components/tables/SpringPagination';
import { Button } from '../../../components/ui/button';
import { cn, formatUnixTimestamp } from '../../../shared/utils';
import { getSafeErrorMessage } from '../../../core/api/response-handler';
import { FaXmark } from 'react-icons/fa6';

export default function MessagesPage() {
  const [activeTab, setActiveTab] = useState<'primite' | 'trimise' | 'creare'>('primite');
  const { userInfo } = useAuthProviderV2();
  const { ROLES } = useRoutesProvider();

  // Check if user is ADMIN or DBEST - only these roles can send messages
  const isAdmin = userInfo?.role === ROLES.ADMIN || userInfo?.role === ROLES.DBEST;

  return (
    <>
      {/* Tab Navigation
          `pt-18 md:pt-4`: hamburgerul plutitor (`ecotic-hamburger-floating`, fixed
          la 20,20, 44x44) se suprapunea cu tab-ul "Primite" (16,16, 95x44) pe
          mobil — masurat, suprapunere reala, nu doar vizual apropiate. Aceeasi
          valoare calibrata ca in SimpleReportLayout, pentru acelasi motiv:
          primul pixel al randului de sub el trebuie sa fie sub hamburger. */}
      <div className="flex gap-2 border-b border-border px-4 pb-4 pt-18 md:pt-4">
        <Button
          variant={activeTab === 'primite' ? 'default' : 'outline'}
          size="lg"
          onClick={() => setActiveTab('primite')}
        >
          Primite
        </Button>
        {isAdmin && (
          <Button
            variant={activeTab === 'trimise' ? 'default' : 'outline'}
            size="lg"
            onClick={() => setActiveTab('trimise')}
          >
            Trimise
          </Button>
        )}
        {isAdmin && (
          <Button
            variant={activeTab === 'creare' ? 'default' : 'outline'}
            size="lg"
            onClick={() => setActiveTab('creare')}
          >
            Creare
          </Button>
        )}
      </div>

      {/* Tab Content */}
      {activeTab === 'primite' && <ReceivedNotificationsTab key="primite" />}
      {activeTab === 'trimise' && isAdmin && <SentNotificationsTab key="trimise" />}
      {activeTab === 'creare' && isAdmin && <SendNotificationPage key="creare" />}
    </>
  );
}

// Received Notifications Tab Component
function ReceivedNotificationsTab() {
  const {
    notifications,
    pagination,
    goToPage,
    changePageSize,
    readNotification,
    openedNotification,
    setOpenedNotification,
    isError,
    error,
    isLoading,
  } = useReceivedNotifications();

  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) =>
      dialogRef.current && !dialogRef.current.contains(event.target as Node)
        ? setOpenedNotification('')
        : null;
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className={'flex flex-col md:h-full md:min-h-0'}>
        <div>
          <PreviewTextBox text={'Mesaje primite'} />
        </div>
        <div className="space-y-4 overflow-x-hidden p-2 md:min-h-0 md:flex-1 md:overflow-y-auto ecotic-table-scrollbar">
          {/* Fara ramura asta, o cerere picata (403-ul din D3) arata exact ca un inbox gol. */}
          {isError && (
            <div
              role="alert"
              className="flex flex-col rounded-lg border-2 border-error bg-error/10 p-4 text-error shadow-md"
            >
              <span className="font-semibold">Mesajele nu au putut fi încărcate.</span>
              <span className="mt-1 text-sm">
                {getSafeErrorMessage(error, 'Încearcă din nou peste câteva momente.')}
              </span>
            </div>
          )}
          {/* Fara gate-ul asta, "Nu ai niciun mesaj primit." apare o clipa la FIECARE
              incarcare initiala — inainte ca `notifications` sa aiba timp sa se populeze —
              exact familia p3c (silent-empty in timpul incarcarii). */}
          {!isError && isLoading && (
            <p className="text-small text-gray-600 m-0">Se încarcă mesajele…</p>
          )}
          {!isError && !isLoading && Array.isArray(notifications) && notifications.length === 0 && (
            <div className="rounded-lg border-2 border-border bg-card p-4 text-muted-foreground shadow-md">
              Nu ai niciun mesaj primit.
            </div>
          )}
          {!isError &&
            !isLoading &&
            Array.isArray(notifications) &&
            notifications.length > 0 &&
            notifications.map((notification, notificationIdx) => (
              <div
                key={notification['id']?.toString() || notificationIdx}
                style={{ animationDelay: `${notificationIdx * 100}ms` }}
                className={cn(
                  !notification['isRead'] ? 'border-error' : 'border-success',
                  'flex animate-fade flex-col rounded-lg border-2 bg-white p-4 shadow-md'
                )}
              >
                <h2 className="text-xl font-semibold">{notification['subject']}</h2>
                <span className="mt-2 text-sm text-muted-foreground">
                  Data primire: {notification['sendAt'] ? formatUnixTimestamp(notification['sendAt']) : 'N/A'}
                </span>
                <span
                  className={cn(
                    !notification['isRead'] ? 'text-error' : 'text-success',
                    'mt-2 text-sm'
                  )}
                >
                  {!notification['isRead'] ? 'NECITIT' : 'CITIT'}
                </span>
                <div className={'flex w-full justify-end'}>
                  <Button
                    variant={'link'}
                    onClick={() => readNotification(notification['id'] || '')}
                  >
                    Deschide
                  </Button>
                </div>
              </div>
            ))}
        </div>
      </div>

      <SpringPagination
        currentPage={pagination.currentPage}
        totalPages={pagination.totalPages}
        totalElements={pagination.totalElements}
        pageSize={pagination.pageSize}
        onPageChange={goToPage}
        onPageSizeChange={changePageSize}
      />

      {openedNotification && (
        <div className="fixed left-0 top-0 z-50 flex h-dvh w-screen animate-fade cursor-default items-center justify-center bg-black/50 backdrop-blur-xs animate-duration-200">
          <div
            ref={dialogRef}
            className="relative flex max-h-[80dvh] w-[calc(100vw-2rem)] flex-col overflow-y-hidden rounded-lg bg-white p-4 shadow-lg sm:w-auto sm:min-w-[40vw] sm:max-w-[80vw]"
          >
            <div className={'relative flex justify-end pb-4'}>
              <button onClick={() => setOpenedNotification('')} className="">
                <FaXmark />
              </button>
            </div>
            <div
              className={
                'relative flex h-full max-h-[80dvh] w-full select-text overflow-y-scroll pr-4'
              }
            >
              <MarkdownPreview text={openedNotification} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Sent Notifications Tab Component
function SentNotificationsTab() {
  const {
    sentNotifications,
    notificationDetails,
    pagination,
    goToPage,
    changePageSize,
    readNotification,
    openDetailedNotificationStatus,
    closeNotificationDetails,
    openedNotification,
    setOpenedNotification,
  } = useSentNotifications();

  const openedNotificationRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <div className={'flex flex-col md:h-full md:min-h-0'}>
        <div>
          <PreviewTextBox text={'Notificari trimise'} />
        </div>
        <div className="space-y-4 overflow-x-hidden p-2 md:min-h-0 md:flex-1 md:overflow-y-auto ecotic-table-scrollbar">
          {Array.isArray(sentNotifications) &&
            sentNotifications.length > 0 &&
            sentNotifications.map((notification, notificationIdx) => notification && (
              <div
                key={notification['id']?.toString() || notificationIdx}
                style={{ animationDelay: `${notificationIdx * 100}ms` }}
                className="flex animate-fade flex-col rounded-lg border-2 border-success bg-card p-4 shadow-md"
              >
                <h2 className="text-xl font-semibold">{notification.notification?.['subject']}</h2>
                <span className="mt-2 text-sm text-muted-foreground">
                  Data trimitere: {notification.notification?.['sendAt'] ? formatUnixTimestamp(notification.notification['sendAt']) : 'N/A'}
                </span>
                <div className={'flex w-full justify-end'}>
                  <Button
                    variant={'link'}
                    onClick={() => openDetailedNotificationStatus(notification.notification?.['id'] || '')}
                  >
                    Detalii
                  </Button>
                  <Button
                    variant={'link'}
                    onClick={() => readNotification(notification.notification?.['id'] || '')}
                  >
                    Deschide
                  </Button>
                </div>
              </div>
            ))}
        </div>
      </div>

      <SpringPagination
        currentPage={pagination.currentPage}
        totalPages={pagination.totalPages}
        totalElements={pagination.totalElements}
        pageSize={pagination.pageSize}
        onPageChange={goToPage}
        onPageSizeChange={changePageSize}
      />

      {openedNotification && (
        <div className="fixed left-0 top-0 z-50 flex h-dvh w-screen animate-fade cursor-default items-center justify-center bg-black/50 backdrop-blur-xs animate-duration-200">
          <div
            ref={openedNotificationRef}
            className="relative flex max-h-[80dvh] w-[calc(100vw-2rem)] flex-col overflow-y-hidden rounded-lg bg-white p-4 shadow-lg sm:w-auto sm:min-w-[40vw] sm:max-w-[80vw]"
          >
            <div className={'relative flex justify-end pb-4'}>
              <button onClick={() => setOpenedNotification('')} className="">
                <FaXmark />
              </button>
            </div>
            <div
              className={
                'relative flex h-full max-h-[80dvh] w-full select-text overflow-y-scroll pr-4'
              }
            >
              <MarkdownPreview text={openedNotification} />
            </div>
          </div>
        </div>
      )}
      {Array.isArray(notificationDetails) && notificationDetails.length > 0 && (
        <div className="fixed left-0 top-0 z-50 flex h-dvh w-screen animate-fade cursor-default items-center justify-center bg-black/50 backdrop-blur-xs animate-duration-200">
          <div
            ref={openedNotificationRef}
            className="relative flex max-h-[80dvh] w-[calc(100vw-2rem)] flex-col overflow-y-hidden rounded-lg bg-white p-4 shadow-lg sm:w-auto sm:min-w-[40vw] sm:max-w-[80vw]"
          >
            <div className={'relative flex justify-end pb-4'}>
              <button onClick={() => closeNotificationDetails()} className="">
                <FaXmark />
              </button>
            </div>
            <div className={'relative flex flex-col space-y-4 overflow-y-scroll pr-4'}>
              {notificationDetails.map((detail, idx) => (
                <div
                  key={idx}
                  className={cn(
                    !detail.isRead ? 'border border-error bg-error/10' : 'border border-success bg-success/10',
                    'rounded-lg p-4 shadow-md'
                  )}
                >
                  <h3 className={'text-md'}>
                    SE Destinatar: <span className={'font-semibold'}>{detail.codSe || ''}</span>
                  </h3>
                  <h4 className={'text-md'}>
                    Institutie invatamant:{' '}
                    <span className={'font-semibold'}>{detail.numeInstitutieInvatamant || ''}</span>
                  </h4>
                  {detail.receiverNume && (
                    <h4 className={'text-md'}>
                      Denumire destinatar:{' '}
                      <span className={'font-semibold'}>{detail.receiverNume || ''}</span>
                    </h4>
                  )}
                  <h4 className={'text-md'}>
                    Tip destinatar:{' '}
                    <span className={'font-semibold'}>{detail.receiverRole || ''}</span>
                  </h4>

                  <p className="text-md">
                    Status Mesaj:{' '}
                    <span className={'font-semibold'}>{detail.isRead ? 'CITIT' : 'NECITIT'}</span>
                  </p>
                  <p className="text-md">
                    Citit la data:{' '}
                    <span className={'font-semibold'}>
                      {detail.readAt ? formatUnixTimestamp(detail.readAt) : 'N/A'}
                    </span>
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
